import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import { AxiosError } from 'axios';

import type { IRiskCenterTabsFilter } from '@app/dashboard/types/innerLayout/risk_center/header';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';
import { LoadingIndicator, PageStatus } from '@unified-checkout/design-system';

import { getSubscriptionData } from '@app/dashboard/services/inner_layout/risk_center/subscriptions';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { RISK_CENTER_TAB_FILTERS } from '@app/dashboard/constants/risk_center';
import {
  RISK_CENTER_EVENTS,
  RISK_CENTER_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/risk-center';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';

import Header from './Header';
import TableListing from './TableListing';
import Subscription from './Subscription';

import styles from './RiskCenter.module.scss';

const RiskCenter = () => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();

  const {
    data: { data: subscriptionData } = {},
    isFetching: isSubscriptionDataLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SUBSCRIPTION_DATA],
    queryFn: () => getSubscriptionData({ isLive }),
    refetchOnMount: true,
    staleTime: 5 * 60 * 1000,
  });

  const [activeTabName, setActiveTabName] = useState<
    IRiskCenterTabsFilter['name']
  >(RISK_CENTER_TAB_FILTERS(t)[0].name);

  const appliedRiskCenterTab =
    RISK_CENTER_TAB_FILTERS(t).find((tab) => tab.name === activeTabName) ||
    RISK_CENTER_TAB_FILTERS(t)[0];

  const errDescription =
    (error as AxiosError<{ detail: string }>)?.response?.data?.detail ||
    t('innerLayout.errors.dataRetrievalErr');

  useEffect(() => {
    amplitude.track(RISK_CENTER_EVENTS.VIEW_RISK_CENTER_PAGE, {
      [SHARED_EVENTS_PARAMS.SUBSCRIPTION]:
        RISK_CENTER_EVENTS_PARAMS.RISK_CENTER,
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
    });
  }, []);

  return isError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={errDescription}
      icon={CircleExclamation}
    />
  ) : isSubscriptionDataLoading ? (
    <div className={styles['full-page-loader']}>
      <LoadingIndicator size="lg" text={t('loading')} />
    </div>
  ) : (
    <>
      {!subscriptionData?.subscribed ? (
        <Subscription />
      ) : (
        <div className={styles['risk-center']}>
          <Header
            appliedRiskCenterTab={appliedRiskCenterTab}
            setAppliedRiskCenterTab={setActiveTabName}
          />
          <TableListing appliedRiskCenterTab={appliedRiskCenterTab} />
        </div>
      )}
    </>
  );
};

export default RiskCenter;
